import { StockStatusEnum } from '@petsmart-ui/sparky';

import type {
    DeliveryMethodType,
    Product,
    ProductApiResponse,
    TailoredApiData,
    VariantKey,
    VariantValues,
} from '@libs/common-types';

import { validateStockStatus } from '../../../utils/stockStatusUtils';

type Combination = Record<string, string>;

export type VariantGroupOption = {
    key: string;
    value: string;
    display: string;
    isChecked: boolean;
    isUnavailable: boolean;
};

export type VariantGroup = {
    name: VariantKey;
    display: string;
    variants: VariantGroupOption[];
};

type FindSkuByCombinationProps = {
    combination: Combination;
    variantToSkuMap: Record<string, string>;
};

type IsVariantInStockProps = {
    sku: string | null;
    product: Product | undefined;
    method: DeliveryMethodType;
    tailored: TailoredApiData | undefined;
};

type MapVariantGroupsProps = {
    activeVariants: VariantValues | null | undefined;
    method: DeliveryMethodType;
    tailored: TailoredApiData | null | undefined;
    uniqueVariantValues: ProductApiResponse['data']['uniqueVariantValues'] | undefined;
    variantToSkuMap: ProductApiResponse['data']['variantToSkuMap'] | undefined;
    variants: Record<string, Product>;
};

export const parseCombinationKey = (combinationKey: string): Combination => {
    const combination: Combination = {};

    combinationKey.split(';').forEach(pair => {
        const [type, value] = pair.split(':');
        if (type && value) {
            combination[type] = value;
        }
    });

    return combination;
};

export const findSkuByCombination = ({ combination, variantToSkuMap }: FindSkuByCombinationProps): string | null => {
    for (const [key, sku] of Object.entries(variantToSkuMap)) {
        const mapCombination = parseCombinationKey(key);

        const isMatch =
            Object.entries(combination).every(
                ([variantType, variantValue]) => mapCombination[variantType] === variantValue
            ) && Object.keys(combination).length === Object.keys(mapCombination).length;

        if (isMatch) return sku;
    }

    return null;
};

export const isVariantCombinationExist = ({ combination, variantToSkuMap }: FindSkuByCombinationProps): boolean => {
    return findSkuByCombination({ combination, variantToSkuMap }) !== null;
};

export const isVariantInStock = ({ sku, product, method, tailored }: IsVariantInStockProps): boolean => {
    if (!sku) return false;

    const tailoredFulfillmentDetails = tailored?.variants[sku]?.[method];
    if (!tailoredFulfillmentDetails) return true;

    const availableQuantity =
        'availableStock' in tailoredFulfillmentDetails && typeof tailoredFulfillmentDetails.availableStock === 'number'
            ? tailoredFulfillmentDetails.availableStock
            : Infinity;
    const minimumQuantity = product?.deliveryMethods[method]?.purchaseQuantities?.min ?? 1;

    const stockStatus = validateStockStatus({
        stockQuantity: availableQuantity,
        minimumCaseQty: minimumQuantity,
        customPharmacyOOS: Boolean(product?.pharmacy?.oos),
    });

    return stockStatus === StockStatusEnum.InStock || stockStatus === StockStatusEnum.FewLeftInStock;
};

export const mapVariantGroups = ({
    activeVariants,
    method,
    tailored,
    uniqueVariantValues,
    variantToSkuMap,
    variants,
}: MapVariantGroupsProps): VariantGroup[] => {
    if (!uniqueVariantValues) return [];

    const selectedValues = activeVariants ?? {};
    const resolvedVariantToSkuMap = variantToSkuMap ?? {};
    const getActiveVariantDisplay = (variantGroup: VariantKey) => {
        const variant = uniqueVariantValues[variantGroup]?.find(value => value.value === selectedValues[variantGroup]);
        return variant?.display || variant?.value || '';
    };
    const getSkuForCombination = (combination: Combination) =>
        findSkuByCombination({ combination, variantToSkuMap: resolvedVariantToSkuMap });
    const doesVariantExist = (combination: Combination) =>
        isVariantCombinationExist({ combination, variantToSkuMap: resolvedVariantToSkuMap });
    const isInStock = (sku: string | null) =>
        isVariantInStock({
            sku,
            product: sku ? variants[sku] : undefined,
            method,
            tailored: tailored ?? undefined,
        });

    return (Object.keys(uniqueVariantValues) as VariantKey[]).map(name => {
        const values = uniqueVariantValues[name] ?? [];

        return {
            name,
            display: getActiveVariantDisplay(name),
            variants: values
                .map(value => {
                    const combination = { ...selectedValues, [name]: value.value };
                    const sku = getSkuForCombination(combination);

                    if (!doesVariantExist(combination)) return null;

                    return {
                        key: value.key,
                        value: value.value,
                        display: value.display ?? value.value,
                        isChecked: selectedValues[name] === value.value,
                        isUnavailable: !isInStock(sku),
                    };
                })
                .filter((value): value is VariantGroupOption => value !== null),
        };
    });
};
